import { Tuple } from "_/types";
import {
  INVALID_SENSOR_TEXTURE_VALUE,
  SENSOR_VALUE_MAX,
  SENSOR_VALUE_MIN,
} from "_/components/analyze/sensor-constants";

export const SENSOR_HISTOGRAM_BIN_COUNT = 1000;
export const SENSOR_HISTOGRAM_BIN_WIDTH =
  (SENSOR_VALUE_MAX - SENSOR_VALUE_MIN) / SENSOR_HISTOGRAM_BIN_COUNT;

export type VisibleHistogramBin = {
  bin: number;
  x: number;
  width: number;
};

const DEFAULT_COLOR_RANGE_WEIGHT = 0.999;

export class SensorHistogramAccumulator {
  readonly bins = new Uint32Array(SENSOR_HISTOGRAM_BIN_COUNT);
  private count = 0;
  private minimum = Infinity;
  private maximum = -Infinity;
  readonly domain: Tuple<number>;
  private readonly isUsableValue: (value: number) => boolean;

  constructor(
    domain: Tuple<number> = [SENSOR_VALUE_MIN, SENSOR_VALUE_MAX],
    isUsableValue = isUsableSensorValue
  ) {
    this.domain = domain;
    this.isUsableValue = isUsableValue;
  }

  get binWidth() {
    return (this.domain[1] - this.domain[0]) / SENSOR_HISTOGRAM_BIN_COUNT;
  }

  get size() {
    return this.count;
  }

  add(value: number) {
    if (!this.isUsableValue(value)) return;

    this.minimum = Math.min(this.minimum, value);
    this.maximum = Math.max(this.maximum, value);

    const normalized =
      (value - this.domain[0]) / (this.domain[1] - this.domain[0]);
    const bin = Math.max(
      0,
      Math.min(
        SENSOR_HISTOGRAM_BIN_COUNT - 1,
        Math.floor(normalized * SENSOR_HISTOGRAM_BIN_COUNT)
      )
    );
    this.bins[bin]++;
    this.count++;
  }

  getFullRange(): Tuple<number> {
    if (this.count === 0) {
      return this.domain;
    }

    if (this.minimum === this.maximum) {
      const halfBinWidth = this.binWidth / 2;
      return [
        Math.max(this.domain[0], this.minimum - halfBinWidth),
        Math.min(this.domain[1], this.maximum + halfBinWidth),
      ];
    }

    return [this.minimum, this.maximum];
  }
}

export function getCentralHistogramRange(
  histogram: Uint32Array,
  fullRange: Tuple<number>,
  domain: Tuple<number> = [SENSOR_VALUE_MIN, SENSOR_VALUE_MAX]
): Tuple<number> {
  const count = histogram.reduce((total, binCount) => total + binCount, 0);
  if (count === 0) return fullRange;

  const tailCount = count * ((1 - DEFAULT_COLOR_RANGE_WEIGHT) / 2);
  const binWidth = (domain[1] - domain[0]) / histogram.length;
  let cumulative = 0;
  let lowerBin = 0;
  let upperBin = histogram.length - 1;

  for (let i = 0; i < histogram.length; i++) {
    cumulative += histogram[i];
    if (cumulative > tailCount) {
      lowerBin = i;
      break;
    }
  }

  cumulative = 0;
  for (let i = histogram.length - 1; i >= 0; i--) {
    cumulative += histogram[i];
    if (cumulative > tailCount) {
      upperBin = i;
      break;
    }
  }

  return [
    Math.max(fullRange[0], domain[0] + lowerBin * binWidth),
    Math.min(fullRange[1], domain[0] + (upperBin + 1) * binWidth),
  ];
}

export function isUsableSensorValue(value: number): boolean {
  return (
    Number.isFinite(value) &&
    value > SENSOR_VALUE_MIN &&
    value < SENSOR_VALUE_MAX
  );
}

export function getSensorTextureValue(value: number): number {
  return isUsableSensorValue(value) ? value : INVALID_SENSOR_TEXTURE_VALUE;
}

export function getVisibleHistogramBins(
  range: Tuple<number>,
  domain: Tuple<number> = [SENSOR_VALUE_MIN, SENSOR_VALUE_MAX]
): VisibleHistogramBin[] {
  const binWidth = (domain[1] - domain[0]) / SENSOR_HISTOGRAM_BIN_COUNT;
  const rangeWidth = range[1] - range[0];
  if (rangeWidth <= 0) return [];

  const firstBin = Math.max(0, Math.floor((range[0] - domain[0]) / binWidth));
  const lastBin = Math.min(
    SENSOR_HISTOGRAM_BIN_COUNT - 1,
    Math.ceil((range[1] - domain[0]) / binWidth) - 1
  );

  return Array.from(
    { length: Math.max(0, lastBin - firstBin + 1) },
    (_, index) => {
      const bin = firstBin + index;
      const binMinimum = domain[0] + bin * binWidth;
      const visibleMinimum = Math.max(range[0], binMinimum);
      const visibleMaximum = Math.min(range[1], binMinimum + binWidth);
      return {
        bin,
        x: (visibleMinimum - range[0]) / rangeWidth,
        width: (visibleMaximum - visibleMinimum) / rangeWidth,
      };
    }
  );
}

export function getRangeInsets(
  domain: Tuple<number>,
  range: Tuple<number>
): Tuple<number> {
  const domainWidth = Math.max(Number.MIN_VALUE, domain[1] - domain[0]);
  return [
    Math.max(0, Math.min(100, ((range[0] - domain[0]) / domainWidth) * 100)),
    Math.max(0, Math.min(100, ((domain[1] - range[1]) / domainWidth) * 100)),
  ];
}

export function zoomRangeAtPosition(
  range: Tuple<number>,
  fullRange: Tuple<number>,
  anchorFraction: number,
  scale: number,
  binWidth = SENSOR_HISTOGRAM_BIN_WIDTH
): Tuple<number> {
  const fullWidth = fullRange[1] - fullRange[0];
  if (fullWidth <= 0) return fullRange;

  const minimumWidth = Math.min(fullWidth, binWidth);
  const fraction = Math.max(0, Math.min(1, anchorFraction));
  const currentWidth = Math.max(minimumWidth, range[1] - range[0]);
  const nextWidth = Math.max(
    minimumWidth,
    Math.min(fullWidth, currentWidth * scale)
  );

  if (nextWidth === fullWidth) return fullRange;

  const anchor = range[0] + fraction * currentWidth;
  let minimum = anchor - fraction * nextWidth;
  minimum = Math.max(fullRange[0], Math.min(fullRange[1] - nextWidth, minimum));

  return [minimum, minimum + nextWidth];
}

export function constrainRange(
  range: Tuple<number>,
  outerRange: Tuple<number>,
  minimumWidth: number
): Tuple<number> {
  const outerWidth = outerRange[1] - outerRange[0];
  if (outerWidth <= 0) return outerRange;

  const width = Math.min(
    outerWidth,
    Math.max(minimumWidth, range[1] - range[0])
  );
  const midpoint = (range[0] + range[1]) / 2;
  const minimum = Math.max(
    outerRange[0],
    Math.min(outerRange[1] - width, midpoint - width / 2)
  );
  return [minimum, minimum + width];
}

export function rescaleNestedRange(
  nestedRange: Tuple<number>,
  previousOuterRange: Tuple<number>,
  nextOuterRange: Tuple<number>
): Tuple<number> {
  const isAtEdge = (value: number, edge: number) =>
    Math.abs(value - edge) <= Number.EPSILON * Math.max(1, Math.abs(edge));
  const lower = isAtEdge(nestedRange[0], previousOuterRange[0])
    ? nextOuterRange[0]
    : nestedRange[0];
  const upper = isAtEdge(nestedRange[1], previousOuterRange[1])
    ? nextOuterRange[1]
    : nestedRange[1];

  return [
    Math.max(nextOuterRange[0], Math.min(nextOuterRange[1], lower)),
    Math.max(nextOuterRange[0], Math.min(nextOuterRange[1], upper)),
  ];
}
